import { useHistory } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import DefaultCard from '../../components/default/DefaultCard';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import DefaultModal from '../../components/default/DefaultModal';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import { toast } from 'react-toastify';
import useNavigationPage from '../../hooks/useNavigationPage';
import { DocumentDataForm, DocumentFilters } from '../../app/models/Document';
import useDocumentService from '../../app/services/hooks/useDocumentService';
import DocumentDataTable from './components/tables/DocumentDataTable';
import DocumentCreate from './components/DocumentCreate';
import DocumentEdit from './components/DocumentEdit';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Documentos',
        url: '/documents',
        isActive: true
    }
];

const Document = () => {
    const { fetchingGetDocuments, getDocuments, deleteDocument, activeDocument } =
        useDocumentService();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    const [showingCreate, setShowingCreate] = useState(false);
    const [showingEdit, setShowingEdit] = useState(false);
    const [documentIdEdit, setDocumentIdEdit] = useState<number>(-1);
    const [documents, setDocuments] = useState<DocumentDataForm[]>([]);
    const [totalRows, setTotalRows] = useState(0);

    const [filter, setFilter] = useState<DocumentFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc',
        document_name: ''
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        getDocuments(filter, {
            onSuccess: (response: ServiceResponse) => {
                setDocuments(response.data.documents);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const active = (documentId: number, active: boolean) => {
        activeDocument(documentId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };
    const destroy = (documentId: number) => {
        const _text = 'Está a punto de eliminar el documento #' + documentId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando documento...');
                        deleteDocument(documentId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El documento no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (documentId: number) => {
        setShowingEdit(true);
        setDocumentIdEdit(documentId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setDocumentIdEdit(-1);
    };

    const onError = () => {
        setShowingEdit(false);
        setDocumentIdEdit(-1);
    };

    return (
        <>
            <Breadcrumbs pageSection="Documentos" breadcrumbs={breadcrumbs} />
            <DefaultCard>
                <div className="row">
                    <div className="col-12">
                        <div className="row">
                            <div className="col">
                                <ButtonCreate callbackCreate={showCreate} title="Nuevo Documento" />
                            </div>
                        </div>
                    </div>
                    <div className="col-12">
                        <DocumentDataTable
                            active={active}
                            documents={documents}
                            totalRows={totalRows}
                            loading={fetchingGetDocuments}
                            destroy={destroy}
                            edit={showEdit}
                            filter={filter}
                            setFilter={setFilter}
                            callbackSuccess={reloadTable}
                            onSearch={reloadTable}
                        />
                    </div>
                </div>
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear nombre de documento"
                    backdrop={true}
                    showFooter={false}
                >
                    <DocumentCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}
            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar documento"
                    backdrop={true}
                    showFooter={false}
                >
                    <DocumentEdit
                        documentId={documentIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Document;
